import { Namespace } from '@36node-fcp/auth-sdk';
import {
  Gantry,
  IllegalAggregationGroup,
  IllegalRecordHandleResult,
  IllegalRecordHandleState,
} from '@36node-fcp/core-sdk';
import { concat, map, orderBy, reduce, uniq } from 'lodash';
import { useCallback, useMemo } from 'react';

import { ROOT_NS } from 'src/config';
import { useIllegalRecordAggregation } from 'src/features/illegal';
import { useNamespaceList } from 'src/features/users';
import { expandPrefixesNs } from 'src/lib/other';

import { useGantryList } from './gantry';

export type ReportRequest = {
  createAt_gt?: string;
  createAt_lt?: string;
  code?: string[];
};

export type GantryReportItem = {
  uniqKey: string;
  gantry: Gantry;
  ns: Namespace;
  illegalCount: number;
  illegalHandleClosedCount: number;
  illegalHandleResult1Count?: number;
  illegalHandleResult2Count?: number;
  illegalHandleResult3Count?: number;
  illegalHandleResult4Count?: number;
  illegalHandleResult5Count?: number;
  illegalHandleResult6Count?: number;
  illegalHandleResult7Count?: number;
};

const buildListForGantryReport = ({
  gantries,
  namespaces,
  illegalAggregations,
  illegalHandleClosedAggregations,
  illegalHandleResult1Aggregations,
  illegalHandleResult2Aggregations,
  illegalHandleResult3Aggregations,
  illegalHandleResult4Aggregations,
  illegalHandleResult5Aggregations,
  illegalHandleResult6Aggregations,
  illegalHandleResult7Aggregations,
}: {
  gantries?: Gantry[];
  namespaces?: Namespace[];
  illegalAggregations?: any[];
  illegalHandleClosedAggregations?: any[];
  illegalHandleResult1Aggregations?: any[];
  illegalHandleResult2Aggregations?: any[];
  illegalHandleResult3Aggregations?: any[];
  illegalHandleResult4Aggregations?: any[];
  illegalHandleResult5Aggregations?: any[];
  illegalHandleResult6Aggregations?: any[];
  illegalHandleResult7Aggregations?: any[];
}): GantryReportItem[] => {
  const gantryIds = uniq(
    concat(
      map(illegalAggregations, 'gantry'),
      map(illegalHandleClosedAggregations, 'gantry'),
      map(illegalHandleResult1Aggregations, 'gantry'),
      map(illegalHandleResult2Aggregations, 'gantry'),
      map(illegalHandleResult3Aggregations, 'gantry'),
      map(illegalHandleResult4Aggregations, 'gantry'),
      map(illegalHandleResult5Aggregations, 'gantry'),
      map(illegalHandleResult6Aggregations, 'gantry'),
      map(illegalHandleResult7Aggregations, 'gantry')
    )
  );

  const list: GantryReportItem[] = [];
  gantryIds.forEach((gantryId) => {
    const gantry = gantries?.find((item) => item.id === gantryId);
    if (gantry) {
      list.push({
        uniqKey: gantryId,
        gantry: gantry,
        ns: namespaces?.find((item) => item.ns === gantry?.ns),
        illegalCount: illegalAggregations?.find((item) => item.gantry === gantryId)?.count || 0,
        illegalHandleClosedCount: illegalHandleClosedAggregations?.find((item) => item.gantry === gantryId)?.count || 0,
        illegalHandleResult1Count:
          illegalHandleResult1Aggregations?.find((item) => item.gantry === gantryId)?.count || 0,
        illegalHandleResult2Count:
          illegalHandleResult2Aggregations?.find((item) => item.gantry === gantryId)?.count || 0,
        illegalHandleResult3Count:
          illegalHandleResult3Aggregations?.find((item) => item.gantry === gantryId)?.count || 0,
        illegalHandleResult4Count:
          illegalHandleResult4Aggregations?.find((item) => item.gantry === gantryId)?.count || 0,
        illegalHandleResult5Count:
          illegalHandleResult5Aggregations?.find((item) => item.gantry === gantryId)?.count || 0,
        illegalHandleResult6Count:
          illegalHandleResult6Aggregations?.find((item) => item.gantry === gantryId)?.count || 0,
        illegalHandleResult7Count:
          illegalHandleResult7Aggregations?.find((item) => item.gantry === gantryId)?.count || 0,
      });
    }
  });
  return orderBy(list, ['illegalHandleClosedCount', 'illegalCount'], ['desc', 'desc']);
};

export const useGantryReport = (req?: ReportRequest) => {
  const [{ result: gantries = [], loading: gantriesLoading, request: gantriesRequest }, listGantries] = useGantryList({
    _limit: 1000,
    _offset: 0,
    _sort: '-createAt',
  });

  const [{ result: namespaces = [], loading: namespacesLoading, request: namespacesRequest }, listNamespaces] =
    useNamespaceList({ ns_start: ROOT_NS, _limit: 1000 });

  const [
    { result: illegalAggregations = [], loading: illegalAggregationsLoading, request: illegalAggregationsRequest },
    listIllegalAggregations,
  ] = useIllegalRecordAggregation({
    _group: [IllegalAggregationGroup.GANTRY],
    _limit: 1000,
    ...req,
  }) as any;

  const [
    {
      result: illegalHandleClosedAggregations = [],
      loading: illegalHandleClosedAggregationsLoading,
      request: illegalHandleClosedAggregationsRequest,
    },
    listIllegalHandleClosedAggregations,
  ] = useIllegalRecordAggregation({
    _group: [IllegalAggregationGroup.GANTRY],
    _limit: 1000,
    handleState: [IllegalRecordHandleState.CLOSED],
    ...req,
  }) as any;

  const [
    {
      result: illegalHandleResult1Aggregations = [],
      loading: illegalHandleResult1AggregationsLoading,
      request: illegalHandleResult1AggregationsRequest,
    },
    listIllegalHandleResult1Aggregations,
  ] = useIllegalRecordAggregation({
    _group: [IllegalAggregationGroup.GANTRY],
    _limit: 1000,
    handleResults: [IllegalRecordHandleResult.NOTIFY_NON_DRIVER_COMPLETED],
    ...req,
  }) as any;

  const [
    {
      result: illegalHandleResult2Aggregations = [],
      loading: illegalHandleResult2AggregationsLoading,
      request: illegalHandleResult2AggregationsRequest,
    },
    listIllegalHandleResult2Aggregations,
  ] = useIllegalRecordAggregation({
    _group: [IllegalAggregationGroup.GANTRY],
    _limit: 1000,
    handleResults: [IllegalRecordHandleResult.NOTIFY_DRIVER_COMPLETED],
    ...req,
  }) as any;

  const [
    {
      result: illegalHandleResult3Aggregations = [],
      loading: illegalHandleResult3AggregationsLoading,
      request: illegalHandleResult3AggregationsRequest,
    },
    listIllegalHandleResult3Aggregations,
  ] = useIllegalRecordAggregation({
    _group: [IllegalAggregationGroup.GANTRY],
    _limit: 1000,
    handleResults: [IllegalRecordHandleResult.PHONE_NOT_CONNECTED],
    ...req,
  }) as any;

  const [
    {
      result: illegalHandleResult4Aggregations = [],
      loading: illegalHandleResult4AggregationsLoading,
      request: illegalHandleResult4AggregationsRequest,
    },
    listIllegalHandleResult4Aggregations,
  ] = useIllegalRecordAggregation({
    _group: [IllegalAggregationGroup.GANTRY],
    _limit: 1000,
    handleResults: [IllegalRecordHandleResult.PHONE_WARNING_EDUCATION],
    ...req,
  }) as any;

  const [
    {
      result: illegalHandleResult5Aggregations = [],
      loading: illegalHandleResult5AggregationsLoading,
      request: illegalHandleResult5AggregationsRequest,
    },
    listIllegalHandleResult5Aggregations,
  ] = useIllegalRecordAggregation({
    _group: [IllegalAggregationGroup.GANTRY],
    _limit: 1000,
    handleResults: [IllegalRecordHandleResult.INTELLIGENCE_BOARD_REMINDER],
    ...req,
  }) as any;

  const [
    {
      result: illegalHandleResult6Aggregations = [],
      loading: illegalHandleResult6AggregationsLoading,
      request: illegalHandleResult6AggregationsRequest,
    },
    listIllegalHandleResult6Aggregations,
  ] = useIllegalRecordAggregation({
    _group: [IllegalAggregationGroup.GANTRY],
    _limit: 1000,
    handleResults: [IllegalRecordHandleResult.PERSUADE_TO_LEAVE_HIGHWAY],
    ...req,
  }) as any;

  const [
    {
      result: illegalHandleResult7Aggregations = [],
      loading: illegalHandleResult7AggregationsLoading,
      request: illegalHandleResult7AggregationsRequest,
    },
    listIllegalHandleResult7Aggregations,
  ] = useIllegalRecordAggregation({
    _group: [IllegalAggregationGroup.GANTRY],
    _limit: 1000,
    handleResults: [IllegalRecordHandleResult.JOINT_INTERVENTION],
    ...req,
  }) as any;

  const loading = useMemo(() => {
    return Boolean(
      gantriesLoading ||
        namespacesLoading ||
        illegalAggregationsLoading ||
        illegalHandleClosedAggregationsLoading ||
        illegalHandleResult1AggregationsLoading ||
        illegalHandleResult2AggregationsLoading ||
        illegalHandleResult3AggregationsLoading ||
        illegalHandleResult4AggregationsLoading ||
        illegalHandleResult5AggregationsLoading ||
        illegalHandleResult6AggregationsLoading ||
        illegalHandleResult7AggregationsLoading
    );
  }, [
    gantriesLoading,
    namespacesLoading,
    illegalAggregationsLoading,
    illegalHandleClosedAggregationsLoading,
    illegalHandleResult1AggregationsLoading,
    illegalHandleResult2AggregationsLoading,
    illegalHandleResult3AggregationsLoading,
    illegalHandleResult4AggregationsLoading,
    illegalHandleResult5AggregationsLoading,
    illegalHandleResult6AggregationsLoading,
    illegalHandleResult7AggregationsLoading,
  ]);

  const result = useMemo(() => {
    return loading
      ? []
      : buildListForGantryReport({
          gantries,
          namespaces,
          illegalAggregations,
          illegalHandleClosedAggregations,
          illegalHandleResult1Aggregations,
          illegalHandleResult2Aggregations,
          illegalHandleResult3Aggregations,
          illegalHandleResult4Aggregations,
          illegalHandleResult5Aggregations,
          illegalHandleResult6Aggregations,
          illegalHandleResult7Aggregations,
        });
  }, [
    loading,
    gantries,
    namespaces,
    illegalAggregations,
    illegalHandleClosedAggregations,
    illegalHandleResult1Aggregations,
    illegalHandleResult2Aggregations,
    illegalHandleResult3Aggregations,
    illegalHandleResult4Aggregations,
    illegalHandleResult5Aggregations,
    illegalHandleResult6Aggregations,
    illegalHandleResult7Aggregations,
  ]);
  const refresh = useCallback(() => {
    listGantries(gantriesRequest);
    listNamespaces(namespacesRequest);
    listIllegalAggregations(illegalAggregationsRequest);
    listIllegalHandleClosedAggregations(illegalHandleClosedAggregationsRequest);
    listIllegalHandleResult1Aggregations(illegalHandleResult1AggregationsRequest);
    listIllegalHandleResult2Aggregations(illegalHandleResult2AggregationsRequest);
    listIllegalHandleResult3Aggregations(illegalHandleResult3AggregationsRequest);
    listIllegalHandleResult4Aggregations(illegalHandleResult4AggregationsRequest);
    listIllegalHandleResult5Aggregations(illegalHandleResult5AggregationsRequest);
    listIllegalHandleResult6Aggregations(illegalHandleResult6AggregationsRequest);
    listIllegalHandleResult7Aggregations(illegalHandleResult7AggregationsRequest);
  }, [
    gantriesRequest,
    namespacesRequest,
    illegalAggregationsRequest,
    illegalHandleClosedAggregationsRequest,
    illegalHandleResult1AggregationsRequest,
    illegalHandleResult2AggregationsRequest,
    illegalHandleResult3AggregationsRequest,
    illegalHandleResult4AggregationsRequest,
    illegalHandleResult5AggregationsRequest,
    illegalHandleResult6AggregationsRequest,
    illegalHandleResult7AggregationsRequest,
  ]);
  return [{ loading, result }, refresh] as const;
};

export type NamespaceReportItem = {
  uniqKey: string;
  ns: Namespace;
  illegalCount: number;
  illegalHandleClosedCount: number;
  illegalHandleResult1Count?: number;
  illegalHandleResult2Count?: number;
  illegalHandleResult3Count?: number;
  illegalHandleResult4Count?: number;
  illegalHandleResult5Count?: number;
  illegalHandleResult6Count?: number;
  illegalHandleResult7Count?: number;
  children?: NamespaceReportItem;
};

/**
 * 将部门列表转换为树结构
 * @param nodes 部门列表
 * @param parent 父级部门
 * @returns NamespaceReportItem[]
 */
const listToTree = (nodes: NamespaceReportItem[], parent?: string): any[] => {
  return nodes
    .filter((node) => node.ns.parent === parent)
    .map((node) => {
      const children = listToTree(nodes, node.ns.ns);
      if (children.length > 0) {
        return {
          ...node,
          illegalCount: reduce(children, (sum, item) => sum + (item.illegalCount ?? 0), node.illegalCount ?? 0),
          illegalHandleClosedCount: reduce(
            children,
            (sum, item) => sum + (item.illegalHandleClosedCount ?? 0),
            node.illegalHandleClosedCount ?? 0
          ),
          illegalHandleResult1Count: reduce(
            children,
            (sum, item) => sum + (item.illegalHandleResult1Count ?? 0),
            node.illegalHandleResult1Count ?? 0
          ),
          illegalHandleResult2Count: reduce(
            children,
            (sum, item) => sum + (item.illegalHandleResult2Count ?? 0),
            node.illegalHandleResult2Count ?? 0
          ),
          illegalHandleResult3Count: reduce(
            children,
            (sum, item) => sum + (item.illegalHandleResult3Count ?? 0),
            node.illegalHandleResult3Count ?? 0
          ),
          illegalHandleResult4Count: reduce(
            children,
            (sum, item) => sum + (item.illegalHandleResult4Count ?? 0),
            node.illegalHandleResult4Count ?? 0
          ),
          illegalHandleResult5Count: reduce(
            children,
            (sum, item) => sum + (item.illegalHandleResult5Count ?? 0),
            node.illegalHandleResult5Count ?? 0
          ),
          illegalHandleResult6Count: reduce(
            children,
            (sum, item) => sum + (item.illegalHandleResult6Count ?? 0),
            node.illegalHandleResult6Count ?? 0
          ),
          illegalHandleResult7Count: reduce(
            children,
            (sum, item) => sum + (item.illegalHandleResult7Count ?? 0),
            node.illegalHandleResult7Count ?? 0
          ),
          children,
        };
      }
      return node;
    });
};

/**
 * 构建部门报表数据列表
 * @param namespaces 全部的部门
 * @param illegalAggregations 违法统计数据
 * @param trackAggregations 过车统计数据
 * @param warningAggregations 预警统计数据
 * @param investigateAggregations 查处统计数据
 * @param subject 违法主体
 * @returns SummaryReportItem[]
 */
const buildListForNamespaceReport = (
  namespaces: Namespace[],
  illegalAggregations: any[],
  illegalHandleClosedAggregations: any[],
  illegalHandleResult1Aggregations: any[],
  illegalHandleResult2Aggregations: any[],
  illegalHandleResult3Aggregations: any[],
  illegalHandleResult4Aggregations: any[],
  illegalHandleResult5Aggregations: any[],
  illegalHandleResult6Aggregations: any[],
  illegalHandleResult7Aggregations: any[]
): NamespaceReportItem[] => {
  const nsIds = expandPrefixesNs(
    uniq(
      concat(
        map(illegalAggregations, 'ns'),
        map(illegalHandleClosedAggregations, 'ns'),
        map(illegalHandleResult1Aggregations, 'ns'),
        map(illegalHandleResult2Aggregations, 'ns'),
        map(illegalHandleResult3Aggregations, 'ns'),
        map(illegalHandleResult4Aggregations, 'ns'),
        map(illegalHandleResult5Aggregations, 'ns'),
        map(illegalHandleResult6Aggregations, 'ns'),
        map(illegalHandleResult7Aggregations, 'ns')
      )
    )
  );

  const list: NamespaceReportItem[] = [];
  nsIds.forEach((nsId) => {
    const ns = namespaces?.find((item) => item.ns === nsId);
    if (ns) {
      list.push({
        uniqKey: nsId,
        ns: ns,
        illegalCount: illegalAggregations?.find((item) => item.ns === nsId)?.count || 0,
        illegalHandleClosedCount: illegalHandleClosedAggregations?.find((item) => item.ns === nsId)?.count || 0,
        illegalHandleResult1Count: illegalHandleResult1Aggregations?.find((item) => item.ns === nsId)?.count || 0,
        illegalHandleResult2Count: illegalHandleResult2Aggregations?.find((item) => item.ns === nsId)?.count || 0,
        illegalHandleResult3Count: illegalHandleResult3Aggregations?.find((item) => item.ns === nsId)?.count || 0,
        illegalHandleResult4Count: illegalHandleResult4Aggregations?.find((item) => item.ns === nsId)?.count || 0,
        illegalHandleResult5Count: illegalHandleResult5Aggregations?.find((item) => item.ns === nsId)?.count || 0,
        illegalHandleResult6Count: illegalHandleResult6Aggregations?.find((item) => item.ns === nsId)?.count || 0,
        illegalHandleResult7Count: illegalHandleResult7Aggregations?.find((item) => item.ns === nsId)?.count || 0,
      });
    }
  });

  return list;
};

/**
 * 获取部门报表需要的统计数据
 * @param req 部门报表查询参数
 * @returns [{ loading, list, result, total }, refresh]
 */
export const useNamespaceReport = (req?: ReportRequest) => {
  const [{ result: namespaces = [], loading: namespacesLoading, request: namespacesRequest }, listNamespaces] =
    useNamespaceList({ ns_start: ROOT_NS, _limit: 1000 });

  const [
    { result: illegalAggregations = [], loading: illegalAggregationsLoading, request: illegalAggregationsRequest },
    listIllegalAggregations,
  ] = useIllegalRecordAggregation({
    _group: [IllegalAggregationGroup.NS],
    _limit: 1000,
    ...req,
  }) as any;

  const [
    {
      result: illegalHandleClosedAggregations = [],
      loading: illegalHandleClosedAggregationsLoading,
      request: illegalHandleClosedAggregationsRequest,
    },
    listIllegalHandleClosedAggregations,
  ] = useIllegalRecordAggregation({
    _group: [IllegalAggregationGroup.NS],
    _limit: 1000,
    handleState: [IllegalRecordHandleState.CLOSED],
    ...req,
  }) as any;

  const [
    {
      result: illegalHandleResult1Aggregations = [],
      loading: illegalHandleResult1AggregationsLoading,
      request: illegalHandleResult1AggregationsRequest,
    },
    listIllegalHandleResult1Aggregations,
  ] = useIllegalRecordAggregation({
    _group: [IllegalAggregationGroup.NS],
    _limit: 1000,
    handleResults: [IllegalRecordHandleResult.NOTIFY_NON_DRIVER_COMPLETED],
    ...req,
  }) as any;

  const [
    {
      result: illegalHandleResult2Aggregations = [],
      loading: illegalHandleResult2AggregationsLoading,
      request: illegalHandleResult2AggregationsRequest,
    },
    listIllegalHandleResult2Aggregations,
  ] = useIllegalRecordAggregation({
    _group: [IllegalAggregationGroup.NS],
    _limit: 1000,
    handleResults: [IllegalRecordHandleResult.NOTIFY_DRIVER_COMPLETED],
    ...req,
  }) as any;

  const [
    {
      result: illegalHandleResult3Aggregations = [],
      loading: illegalHandleResult3AggregationsLoading,
      request: illegalHandleResult3AggregationsRequest,
    },
    listIllegalHandleResult3Aggregations,
  ] = useIllegalRecordAggregation({
    _group: [IllegalAggregationGroup.NS],
    _limit: 1000,
    handleResults: [IllegalRecordHandleResult.PHONE_NOT_CONNECTED],
    ...req,
  }) as any;

  const [
    {
      result: illegalHandleResult4Aggregations = [],
      loading: illegalHandleResult4AggregationsLoading,
      request: illegalHandleResult4AggregationsRequest,
    },
    listIllegalHandleResult4Aggregations,
  ] = useIllegalRecordAggregation({
    _group: [IllegalAggregationGroup.NS],
    _limit: 1000,
    handleResults: [IllegalRecordHandleResult.PHONE_WARNING_EDUCATION],
    ...req,
  }) as any;

  const [
    {
      result: illegalHandleResult5Aggregations = [],
      loading: illegalHandleResult5AggregationsLoading,
      request: illegalHandleResult5AggregationsRequest,
    },
    listIllegalHandleResult5Aggregations,
  ] = useIllegalRecordAggregation({
    _group: [IllegalAggregationGroup.NS],
    _limit: 1000,
    handleResults: [IllegalRecordHandleResult.INTELLIGENCE_BOARD_REMINDER],
    ...req,
  }) as any;

  const [
    {
      result: illegalHandleResult6Aggregations = [],
      loading: illegalHandleResult6AggregationsLoading,
      request: illegalHandleResult6AggregationsRequest,
    },
    listIllegalHandleResult6Aggregations,
  ] = useIllegalRecordAggregation({
    _group: [IllegalAggregationGroup.NS],
    _limit: 1000,
    handleResults: [IllegalRecordHandleResult.PERSUADE_TO_LEAVE_HIGHWAY],
    ...req,
  }) as any;

  const [
    {
      result: illegalHandleResult7Aggregations = [],
      loading: illegalHandleResult7AggregationsLoading,
      request: illegalHandleResult7AggregationsRequest,
    },
    listIllegalHandleResult7Aggregations,
  ] = useIllegalRecordAggregation({
    _group: [IllegalAggregationGroup.NS],
    _limit: 1000,
    handleResults: [IllegalRecordHandleResult.JOINT_INTERVENTION],
    ...req,
  }) as any;

  const loading = useMemo(() => {
    return Boolean(
      namespacesLoading ||
        illegalAggregationsLoading ||
        illegalHandleClosedAggregationsLoading ||
        illegalHandleResult1AggregationsLoading ||
        illegalHandleResult2AggregationsLoading ||
        illegalHandleResult3AggregationsLoading ||
        illegalHandleResult4AggregationsLoading ||
        illegalHandleResult5AggregationsLoading ||
        illegalHandleResult6AggregationsLoading ||
        illegalHandleResult7AggregationsLoading
    );
  }, [
    namespacesLoading,
    illegalAggregationsLoading,
    illegalHandleClosedAggregationsLoading,
    illegalHandleResult1AggregationsLoading,
    illegalHandleResult2AggregationsLoading,
    illegalHandleResult3AggregationsLoading,
    illegalHandleResult4AggregationsLoading,
    illegalHandleResult5AggregationsLoading,
    illegalHandleResult6AggregationsLoading,
    illegalHandleResult7AggregationsLoading,
  ]);

  const result = useMemo(() => {
    return loading
      ? []
      : buildListForNamespaceReport(
          namespaces,
          illegalAggregations,
          illegalHandleClosedAggregations,
          illegalHandleResult1Aggregations,
          illegalHandleResult2Aggregations,
          illegalHandleResult3Aggregations,
          illegalHandleResult4Aggregations,
          illegalHandleResult5Aggregations,
          illegalHandleResult6Aggregations,
          illegalHandleResult7Aggregations
        );
  }, [
    loading,
    namespaces,
    illegalAggregations,
    illegalAggregations,
    illegalHandleClosedAggregations,
    illegalHandleResult1Aggregations,
    illegalHandleResult2Aggregations,
    illegalHandleResult3Aggregations,
    illegalHandleResult4Aggregations,
    illegalHandleResult5Aggregations,
    illegalHandleResult6Aggregations,
    illegalHandleResult7Aggregations,
  ]);
  const refresh = useCallback(() => {
    listNamespaces(namespacesRequest);
    listIllegalAggregations(illegalAggregationsRequest);
    listIllegalHandleClosedAggregations(illegalHandleClosedAggregationsRequest);
    listIllegalHandleResult1Aggregations(illegalHandleResult1AggregationsRequest);
    listIllegalHandleResult2Aggregations(illegalHandleResult2AggregationsRequest);
    listIllegalHandleResult3Aggregations(illegalHandleResult3AggregationsRequest);
    listIllegalHandleResult4Aggregations(illegalHandleResult4AggregationsRequest);
    listIllegalHandleResult5Aggregations(illegalHandleResult5AggregationsRequest);
    listIllegalHandleResult6Aggregations(illegalHandleResult6AggregationsRequest);
    listIllegalHandleResult7Aggregations(illegalHandleResult7AggregationsRequest);
  }, [
    namespacesRequest,
    illegalAggregationsRequest,
    illegalHandleClosedAggregationsRequest,
    illegalHandleResult1AggregationsRequest,
    illegalHandleResult2AggregationsRequest,
    illegalHandleResult3AggregationsRequest,
    illegalHandleResult4AggregationsRequest,
    illegalHandleResult5AggregationsRequest,
    illegalHandleResult6AggregationsRequest,
    illegalHandleResult7AggregationsRequest,
  ]);
  return [
    {
      loading,
      list: result.filter((item) => item.illegalCount),
      result: listToTree(result, map(result, 'uniqKey').includes(ROOT_NS) ? undefined : ROOT_NS),
      total: result.filter((item) => item.illegalCount).length,
    },
    refresh,
  ] as const;
};
